import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { Plus, Eye } from 'lucide-react';

const today = new Date().toISOString().split('T')[0];
const inr = v => `₹${Number(v||0).toLocaleString('en-IN', {minimumFractionDigits:2})}`;

const statusBadge = s => {
  const map = { DRAFT:'badge-muted', ORDERED:'badge-blue', RECEIVED:'badge-green', PARTIAL:'badge-amber', CANCELLED:'badge-red' };
  return <span className={`badge ${map[s]||'badge-muted'}`}>{s}</span>;
};

const UNITS = ['NOS','KG','LITRE','METER','TON','BAG','BOX','OTHER'];

export default function PurchasePage() {
  const [view, setView]           = useState('list');
  const [orders, setOrders]       = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selected, setSelected]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSupplier, setShowSupplier] = useState(false);
  const [showPayment, setShowPayment]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [msg, setMsg]             = useState('');
  const [form, setForm] = useState({ supplier:'', order_date:today, expected_date:'', gst_percent:'18', notes:'' });
  const [suppForm, setSuppForm] = useState({ name:'', contact_person:'', phone:'', email:'', address:'', gstin:'' });
  const [itemForm, setItemForm] = useState({ item:'', item_name:'', quantity:'', unit:'NOS', unit_price:'', notes:'' });
  const [addingItem, setAddingItem] = useState(false);
  const [categories, setCategories] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [payForm, setPayForm] = useState({ amount:'', payment_mode:'CASH', payment_date:today, reference:'', notes:'' });

  const load = useCallback(() => {
    setLoading(true);
    api.get('/purchase/orders/?page_size=100').then(r => setOrders(r.data.results||[])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/purchase/suppliers/?page_size=100').then(r => setSuppliers(r.data.results||[])); }, []);
  useEffect(() => {
    api.get('/purchase/categories/').then(r => setCategories(r.data.results||r.data||[]));
    api.get('/purchase/items/?page_size=500').then(r => setAllItems(r.data.results||[]));
  }, []);

  const loadDetail = async (po) => {
    try {
      const res = await api.get(`/purchase/orders/${po.id}/`);
      setSelected(res.data?.data || res.data);
    } catch { setSelected(po); }
    setView('detail');
  };

  const handleCreate = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.post('/purchase/orders/', form);
      setShowModal(false); load();
      setMsg('Purchase order created!'); setTimeout(() => setMsg(''), 3000);
      setForm({ supplier:'', order_date:today, expected_date:'', gst_percent:'18', notes:'' });
    } catch(err) { setError(err.response?.data?.errors ? JSON.stringify(err.response.data.errors) : 'Failed.'); }
    finally { setSaving(false); }
  };

  const handleCreateSupplier = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.post('/purchase/suppliers/', suppForm);
      setShowSupplier(false);
      api.get('/purchase/suppliers/?page_size=100').then(r => setSuppliers(r.data.results||[]));
      setMsg('Supplier added!'); setTimeout(() => setMsg(''), 3000);
      setSuppForm({ name:'', contact_person:'', phone:'', email:'', address:'', gstin:'' });
    } catch(err) { setError(err.response?.data?.errors ? JSON.stringify(err.response.data.errors) : 'Failed.'); }
    finally { setSaving(false); }
  };

  const handleAddItem = async () => {
    if(!itemForm.item||!itemForm.quantity||!itemForm.unit_price) { alert('Please select category, item, enter quantity and price.'); return; }
    setAddingItem(true);
    try {
      await api.post(`/purchase/orders/${selected.id}/items/`, itemForm);
      setItemForm({ item:'', item_name:'', quantity:'', unit:'NOS', unit_price:'', notes:'' });
      setSelectedCategory('');
      setFilteredItems([]);
      loadDetail(selected);
    } catch(err) { alert(err.response?.data ? JSON.stringify(err.response.data) : 'Failed to add item.'); }
    finally { setAddingItem(false); }
  };

  const handlePayment = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post(`/purchase/orders/${selected.id}/payments/`, payForm);
      setShowPayment(false); loadDetail(selected);
      setMsg('Payment recorded!'); setTimeout(() => setMsg(''), 3000);
      setPayForm({ amount:'', payment_mode:'CASH', payment_date:today, reference:'', notes:'' });
    } catch(err) { 
      const msg = err.response?.data?.errors ? JSON.stringify(err.response.data.errors) : 
                  err.response?.data?.detail || err.response?.data?.message || 
                  JSON.stringify(err.response?.data) || 'Failed to record payment.';
      alert(msg); 
    }
    finally { setSaving(false); }
  };

  const handleStatusChange = async (newStatus) => {
    try { 
      await api.patch(`/purchase/orders/${selected.id}/`, { status: newStatus });
      if(newStatus === 'RECEIVED') {
        // Force stock update for all items
        const items = selected.items || [];
        for(const item of items) {
          if(item.item && item.received_qty === 0) {
            await api.post(`/purchase/orders/${selected.id}/receive/`).catch(()=>{});
          }
        }
      }
      loadDetail(selected); 
    } catch {}
  };

  if(view === 'detail' && selected) {
    const items = selected.items || [];
    const payments = selected.payments || [];
    return (
      <Layout title={selected.po_number} subtitle={`Supplier: ${selected.supplier_name||'—'}`}
        actions={<button className="btn btn-secondary" onClick={()=>setView('list')}>← Back</button>}
      >
        {msg && <div className="success-box">{msg}</div>}
        <div className="kpi-grid" style={{marginBottom:16}}>
          <div className="kpi-card brand"><div className="kpi-value">{inr(selected.total_amount)}</div><div className="kpi-label">Total</div></div>
          <div className="kpi-card green"><div className="kpi-value">{inr(selected.paid_amount)}</div><div className="kpi-label">Paid</div></div>
          <div className="kpi-card red"><div className="kpi-value">{inr(selected.balance_amount)}</div><div className="kpi-label">Balance</div></div>
          <div className="kpi-card amber"><div className="kpi-value">{statusBadge(selected.status)}</div><div className="kpi-label">Status</div></div>
        </div>

        <div className="card" style={{marginBottom:16}}>
          <div style={{fontSize:11,fontWeight:700,letterSpacing:'1.2px',textTransform:'uppercase',color:'var(--brand)',borderBottom:'2px solid var(--border)',paddingBottom:8,marginBottom:14}}>PO Details</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))',gap:16,marginBottom:16}}>
            {[['PO Number',selected.po_number],['Supplier',selected.supplier_name||'—'],['Order Date',selected.order_date],['Expected Date',selected.expected_date||'—'],['GST %',`${selected.gst_percent}%`],['Notes',selected.notes||'—']].map(([l,v])=>(
              <div key={l} style={{display:'flex',flexDirection:'column',gap:4}}>
                <span style={{fontSize:11,color:'var(--text-muted)',fontWeight:600,textTransform:'uppercase'}}>{l}</span>
                <span style={{fontSize:13,fontWeight:500}}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
            {['DRAFT','ORDERED','RECEIVED','CANCELLED'].filter(s=>s!==selected.status).map(s=>(
              <button key={s} className="btn btn-secondary btn-sm" onClick={()=>handleStatusChange(s)}>Mark as {s}</button>
            ))}
            <button className="btn btn-primary btn-sm" onClick={()=>setShowPayment(true)}>+ Record Payment</button>
          </div>
        </div>

        <div className="card" style={{marginBottom:16}}>
          <div style={{fontSize:11,fontWeight:700,letterSpacing:'1.2px',textTransform:'uppercase',color:'var(--brand)',borderBottom:'2px solid var(--border)',paddingBottom:8,marginBottom:14}}>Items</div>
          <div style={{background:'var(--bg-primary)',borderRadius:'var(--radius-sm)',padding:16,marginBottom:16,border:'1px solid var(--border)'}}>
            <div style={{fontSize:12,fontWeight:600,marginBottom:10,color:'var(--text-secondary)'}}>Add Item</div>
            <div style={{display:'flex',flexWrap:'wrap',gap:10,alignItems:'end'}}>
              <div className="form-group" style={{marginBottom:0, flex:'1 1 140px'}}>
                <label className="form-label">Category *</label>
                <select className="form-input" value={selectedCategory} onChange={e=>{
                  const catId = e.target.value;
                  setSelectedCategory(catId);
                  setItemForm(f=>({...f,item:'',unit:'NOS',unit_price:''}));
                  if(catId) {
                    api.get(`/purchase/items/?category=${catId}&page_size=200`).then(r=>{
                      setFilteredItems(r.data.results||[]);
                    });
                  } else {
                    setFilteredItems([]);
                  }
                }}>
                  <option value="">Select Category</option>
                  {categories.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group" style={{marginBottom:0, flex:'1 1 140px'}}>
                <label className="form-label">Item *</label>
                <select className="form-input" value={itemForm.item} onChange={e=>{
                  const item = filteredItems.find(i=>i.id===e.target.value);
                  setItemForm(f=>({...f, item:e.target.value, unit:item?.unit||'NOS', unit_price:item?.last_price||''}));
                }}>
                  <option value="">Select Item</option>
                  {filteredItems.map(i=><option key={i.id} value={i.id}>{i.name}</option>)}
                </select>
              </div>
              <div className="form-group" style={{marginBottom:0, flex:'1 1 80px'}}>
                <label className="form-label">Qty *</label>
                <input className="form-input" type="number" value={itemForm.quantity} onChange={e=>setItemForm(f=>({...f,quantity:e.target.value}))}/>
              </div>
              <div className="form-group" style={{marginBottom:0, flex:'1 1 140px'}}>
                <label className="form-label">Unit Price (₹) *</label>
                <input className="form-input" type="number" value={itemForm.unit_price} onChange={e=>setItemForm(f=>({...f,unit_price:e.target.value}))}/>
              </div>
              <button className="btn btn-primary" style={{flex:'0 0 auto'}} onClick={handleAddItem} disabled={addingItem}>{addingItem?'...':'+ Add'}</button>
            </div>
          </div>
          {items.length===0 ? (
            <div style={{textAlign:'center',padding:20,color:'var(--text-muted)'}}>No items added yet.</div>
          ) : (
            <>
              <div style={{overflowX:'auto'}}>
                <table>
                  <thead><tr><th>S No</th><th>Item</th><th>Qty</th><th>Unit</th><th>Unit Price</th><th>Total</th></tr></thead>
                  <tbody>
                    {items.map((item,i)=>(
                      <tr key={i}>
                        <td>{i+1}</td>
                        <td style={{fontWeight:600}}>{item.item_display||item.item_name||item.name||'—'}</td>
                        <td style={{fontFamily:'DM Mono,monospace'}}>{item.quantity}</td>
                        <td>{item.item_unit||item.unit||'—'}</td>
                        <td style={{fontFamily:'DM Mono,monospace'}}>{inr(item.unit_price)}</td>
                        <td style={{fontFamily:'DM Mono,monospace',fontWeight:700,color:'var(--brand)'}}>{inr(item.total_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',padding:'12px 0',gap:6,borderTop:'1px solid var(--border)'}}>
                <div style={{fontSize:13,color:'var(--text-secondary)'}}>Subtotal: <strong>{inr(selected.subtotal)}</strong></div>
                <div style={{fontSize:13,color:'var(--text-secondary)'}}>GST ({selected.gst_percent}%): <strong>{inr(selected.gst_amount)}</strong></div>
                <div style={{fontSize:16,fontWeight:700,color:'var(--brand)'}}>Total: {inr(selected.total_amount)}</div>
              </div>
            </>
          )}
        </div>

        <div className="card">
          <div style={{fontSize:11,fontWeight:700,letterSpacing:'1.2px',textTransform:'uppercase',color:'var(--brand)',borderBottom:'2px solid var(--border)',paddingBottom:8,marginBottom:14}}>Payment History</div>
          {payments.length===0 ? (
            <div style={{textAlign:'center',padding:20,color:'var(--text-muted)'}}>No payments recorded.</div>
          ) : (
            <div style={{overflowX:'auto'}}>
            <table>
              <thead><tr><th>Date</th><th>Amount</th><th>Mode</th><th>Reference</th><th>Notes</th></tr></thead>
              <tbody>
                {payments.map((p,i)=>(
                  <tr key={i}>
                    <td style={{fontFamily:'DM Mono,monospace',fontSize:12}}>{p.payment_date}</td>
                    <td style={{fontFamily:'DM Mono,monospace',fontWeight:700,color:'var(--green)'}}>{inr(p.amount)}</td>
                    <td><span className="badge badge-blue">{p.payment_mode}</span></td>
                    <td style={{fontSize:12}}>{p.reference||'—'}</td>
                    <td style={{fontSize:12,color:'var(--text-muted)'}}>{p.notes||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      

        {showPayment && (
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
            <div style={{background:'var(--white)',borderRadius:'var(--radius)',padding:24,width:460,maxWidth:'90vw'}}>
              <div style={{fontSize:15,fontWeight:600,marginBottom:16}}>Record Payment</div>
              <div style={{padding:'10px 14px',background:'var(--brand-dim)',borderRadius:'var(--radius-sm)',marginBottom:14,fontSize:12,color:'var(--brand)',fontWeight:600}}>
                Balance Due: {inr(selected.balance_amount)}
              </div>
              <form onSubmit={handlePayment}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
                  <div className="form-group"><label className="form-label">Amount (₹) *</label><input className="form-input" required type="number" step="0.01" value={payForm.amount} onChange={e=>setPayForm(f=>({...f,amount:e.target.value}))}/></div>
                  <div className="form-group"><label className="form-label">Payment Mode</label>
                    <select className="form-input" value={payForm.payment_mode} onChange={e=>setPayForm(f=>({...f,payment_mode:e.target.value}))}>
                      {['CASH','BANK','CHEQUE','UPI'].map(s=><option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Payment Date</label><input className="form-input" type="date" value={payForm.payment_date} onChange={e=>setPayForm(f=>({...f,payment_date:e.target.value}))}/></div>
                  <div className="form-group"><label className="form-label">Reference #</label><input className="form-input" value={payForm.reference} onChange={e=>setPayForm(f=>({...f,reference:e.target.value}))} placeholder="Cheque/UTR number"/></div>
                  <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={payForm.notes} onChange={e=>setPayForm(f=>({...f,notes:e.target.value}))}/></div>
                </div>
                <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
                  <button type="button" className="btn btn-secondary" onClick={()=>setShowPayment(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>{saving?'Saving...':'Record Payment'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </Layout>
    );
  }

  return (
    <Layout title="Purchase" subtitle="Supplier orders and inventory management"
      actions={
        <div style={{display:'flex',gap:10}}>
          <button className="btn btn-secondary" onClick={()=>{setShowSupplier(true);setError('');}}><Plus size={14}/>Add Supplier</button>
          <button className="btn btn-primary" onClick={()=>{setShowModal(true);setError('');}}><Plus size={14}/>New PO</button>
        </div>
      }
    >
      {msg && <div className="success-box">{msg}</div>}
      <div className="kpi-grid" style={{marginBottom:16}}>
        <div className="kpi-card brand"><div className="kpi-value">{orders.length}</div><div className="kpi-label">Total POs</div></div>
        <div className="kpi-card amber"><div className="kpi-value">{orders.filter(o=>o.status==='ORDERED').length}</div><div className="kpi-label">Ordered</div></div>
        <div className="kpi-card green"><div className="kpi-value">{orders.filter(o=>o.status==='RECEIVED').length}</div><div className="kpi-label">Received</div></div>
        <div className="kpi-card red"><div className="kpi-value">{inr(orders.reduce((s,o)=>s+parseFloat(o.balance_amount||0),0))}</div><div className="kpi-label">Balance Due</div></div>
      </div>
      <div className="card">
        {loading ? <div className="loading"><div className="spinner"/>Loading...</div> :
          orders.length===0 ? <div className="empty-state"><p>No purchase orders found.</p></div> :
          <div className="table-wrap">
            <table>
              <thead><tr><th>PO #</th><th>Supplier</th><th>Date</th><th>Items</th><th>Total</th><th>Paid</th><th>Balance</th><th>Status</th><th>Action</th></tr></thead>
              <tbody>
                {orders.map(o=>(
                  <tr key={o.id}>
                    <td style={{color:'var(--brand)',fontWeight:600}}>{o.po_number}</td>
                    <td style={{fontWeight:600}}>{o.supplier_name}</td>
                    <td style={{fontFamily:'DM Mono,monospace',fontSize:12}}>{o.order_date}</td>
                    <td><span className="badge badge-blue">{o.item_count||0}</span></td>
                    <td style={{fontFamily:'DM Mono,monospace',fontWeight:600}}>{inr(o.total_amount)}</td>
                    <td style={{fontFamily:'DM Mono,monospace',color:'var(--green)'}}>{inr(o.paid_amount)}</td>
                    <td style={{fontFamily:'DM Mono,monospace',color:o.balance_amount>0?'var(--red)':'var(--green)'}}>{inr(o.balance_amount)}</td>
                    <td>{statusBadge(o.status)}</td>
                    <td><button className="btn btn-sm btn-secondary" onClick={()=>loadDetail(o)}><Eye size={12}/>View</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div className="modal">
            <div className="modal-header"><h3>Create Purchase Order</h3><button onClick={()=>setShowModal(false)} style={{background:'none',border:'none',cursor:'pointer',fontSize:18}}>×</button></div>
            <form onSubmit={handleCreate}>
              <div className="modal-body">
                {error && <div className="error-box">{error}</div>}
                <div className="form-grid">
                  <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Supplier *</label>
                    <select className="form-input" required value={form.supplier} onChange={e=>setForm({...form,supplier:e.target.value})}>
                      <option value="">Select Supplier</option>
                      {suppliers.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Order Date</label><input className="form-input" type="date" value={form.order_date} onChange={e=>setForm({...form,order_date:e.target.value})}/></div>
                  <div className="form-group"><label className="form-label">Expected Date</label><input className="form-input" type="date" value={form.expected_date} onChange={e=>setForm({...form,expected_date:e.target.value})}/></div>
                  <div className="form-group"><label className="form-label">GST %</label><input className="form-input" type="number" value={form.gst_percent} onChange={e=>setForm({...form,gst_percent:e.target.value})}/></div>
                  <div className="form-group"><label className="form-label">Notes</label><input className="form-input" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}/></div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving?'Creating...':'Create PO'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSupplier && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowSupplier(false)}>
          <div className="modal">
            <div className="modal-header"><h3>Add Supplier</h3><button onClick={()=>setShowSupplier(false)} style={{background:'none',border:'none',cursor:'pointer',fontSize:18}}>×</button></div>
            <form onSubmit={handleCreateSupplier}>
              <div className="modal-body">
                {error && <div className="error-box">{error}</div>}
                <div className="form-grid">
                  <div className="form-group"><label className="form-label">Supplier Name *</label><input className="form-input" required value={suppForm.name} onChange={e=>setSuppForm({...suppForm,name:e.target.value})}/></div>
                  <div className="form-group"><label className="form-label">Contact Person</label><input className="form-input" value={suppForm.contact_person} onChange={e=>setSuppForm({...suppForm,contact_person:e.target.value})}/></div>
                  <div className="form-group"><label className="form-label">Phone *</label><input className="form-input" required value={suppForm.phone} onChange={e=>setSuppForm({...suppForm,phone:e.target.value})} maxLength={15}/></div>
                  <div className="form-group"><label className="form-label">GSTIN</label><input className="form-input" value={suppForm.gstin} onChange={e=>setSuppForm({...suppForm,gstin:e.target.value.toUpperCase()})} maxLength={15} placeholder="33AAKCT5967G1ZY"/></div>
                  <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Address</label><textarea className="form-input" rows={2} value={suppForm.address} onChange={e=>setSuppForm({...suppForm,address:e.target.value})}/></div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowSupplier(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving?'Saving...':'Add Supplier'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}